/** Lip Sync 22xxxx 埋点公共字段与工具（见 docs/lipsync-analytics.md） */

import { isLogstoreEnabled } from "~/utils/analyticsEnv.js";

const PROMPT_LOG_MAX = 2000;
const PAY_PLAN_CLICK_STORAGE_KEY = "lip_pay_plan_click";

export function truncateAnalyticsPrompt(text, maxLen = PROMPT_LOG_MAX) {
  if (text == null) return "";
  const s = String(text);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

/** 登录用户用真实 user_id；匿名用户将 uuid 写入 user_id（不单独上报 uuid 字段） */
export function resolveLipSyncUserId(userStore) {
  if (!import.meta.client) return "";
  try {
    const stored = localStorage.getItem("user_id");
    if (stored) return String(stored);
  } catch {
    /* ignore */
  }
  return userStore?.uuid ? String(userStore.uuid) : "";
}

export function getLipSyncLogBase({ userStore, route } = {}) {
  const base = {
    has_paid_credits: Boolean(userStore?.has_paid_credits),
    user_type: resolveLipSyncUserType(userStore),
  };
  if (import.meta.client) {
    base.url = window.location.href;
    base.route = route?.path ?? window.location.pathname;
    const userId = resolveLipSyncUserId(userStore);
    if (userId) base.user_id = userId;
  }
  return base;
}

function resolveLipSyncUserType(userStore) {
  if (!userStore?.token) return "anonymous";
  return userStore.has_paid_credits ? "paid" : "free";
}

function readPayPlanClickContext() {
  if (!import.meta.client) return null;
  try {
    const raw =
      localStorage.getItem(PAY_PLAN_CLICK_STORAGE_KEY) ||
      sessionStorage.getItem(PAY_PLAN_CLICK_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function trackLipSyncEvent(sendLog, eventId, payload = {}, ctx = {}) {
  if (!import.meta.client || typeof sendLog !== "function") return;
  const config = useRuntimeConfig();
  if (!isLogstoreEnabled(config)) return;
  sendLog(eventId, {
    ...getLipSyncLogBase(ctx),
    ...payload,
  });
}

/** `220017` — 积分不足（见 docs/lipsync-analytics.md §4.17） */
export function trackInsufficientCredits(sendLog, payload = {}, ctx = {}) {
  trackLipSyncEvent(
    sendLog,
    "0017",
    {
      type: "insufficient_credits",
      ...payload,
    },
    ctx,
  );
}

/** `220018` — 图片/视频校验不通过（见 docs/lipsync-analytics.md §4.18） */
export function trackMediaComplianceFail(sendLog, payload = {}, ctx = {}) {
  trackLipSyncEvent(
    sendLog,
    "0018",
    {
      type: "media_compliance_fail",
      ...payload,
    },
    ctx,
  );
}

export function isLoggedInCreditsInsufficient(userStore, estimatedCredits) {
  const needed = Number(estimatedCredits) || 0;
  if (needed <= 0) return false;
  const available = Number(userStore?.credits) || 0;
  return available < needed;
}

export function parseAnalyticsTaskParams(params) {
  if (!params) return {};
  if (typeof params === "string") {
    try {
      const parsed = JSON.parse(params);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return params;
}

/** 历史卡片编辑埋点 `detail`：任务元数据 + 解析后的 params */
export function buildHistoryTaskEditDetail(item) {
  if (!item || typeof item !== "object") return {};
  const taskParams = parseAnalyticsTaskParams(item.params);
  return {
    task_id: item.task_id ?? "",
    status: item.status,
    preview: item.preview ?? "",
    create_time:
      item.create_time ?? item.created_at ?? item.createdAt ?? item.update_time ?? "",
    model_name: item.modelName ?? "",
    ...taskParams,
  };
}

/** `220021` — 历史记录点击编辑（见 docs/lipsync-analytics.md §4.21） */
export function trackHistoryEdit(sendLog, item, ctx = {}) {
  trackLipSyncEvent(
    sendLog,
    "0021",
    {
      type: "edit",
      detail: buildHistoryTaskEditDetail(item),
    },
    ctx,
  );
}

/** `220016` — 点击下载（见 docs/lipsync-analytics.md §4.16） */
export function trackLipSyncDownload(sendLog, payload = {}, ctx = {}) {
  const task_id = payload.task_id != null ? String(payload.task_id).trim() : "";
  trackLipSyncEvent(
    sendLog,
    "0016",
    {
      type: "download",
      download_url: payload.download_url ?? "",
      status: payload.status === "success" ? "success" : "filed",
      ...(task_id ? { task_id } : {}),
      ...(payload.massage ? { massage: String(payload.massage) } : {}),
    },
    ctx,
  );
}

/**
 * fetch + blob 下载并上报 `220016`。
 * @param {string} [taskId] 任务 ID，写入 `220016` 的 `task_id` 字段
 * @returns {Promise<void>}
 */
export async function downloadLipSyncVideoWithAnalytics(
  sendLog,
  ctx,
  url,
  fileName = "video.mp4",
  taskId = "",
) {
  const download_url = url ? String(url) : "";
  const task_id = taskId != null ? String(taskId).trim() : "";
  const downloadPayload = { download_url, ...(task_id ? { task_id } : {}) };
  if (!download_url) {
    trackLipSyncDownload(
      sendLog,
      { ...downloadPayload, status: "filed", massage: "missing url" },
      ctx,
    );
    return Promise.reject(new Error("missing url"));
  }
  try {
    const { downloadLipSyncVideoBlob } = await import("~/utils/lipSyncVideoDownload.js");
    await downloadLipSyncVideoBlob(download_url, fileName);
    trackLipSyncDownload(sendLog, { ...downloadPayload, status: "success" }, ctx);
  } catch (err) {
    const massage = err?.message ? String(err.message) : String(err);
    trackLipSyncDownload(sendLog, { ...downloadPayload, status: "filed", massage }, ctx);
    throw err;
  }
}

export function avatarTemplateDetail(avatar) {
  if (!avatar) return {};
  return {
    country: avatar.country ?? avatar.region ?? "",
    ethnicity: avatar.ethnicity ?? "",
    age: avatar.age ?? "",
    gender: avatar.gender ?? "",
  };
}

export function voiceTemplateDetail(voice) {
  if (!voice) return {};
  return {
    language: voice.language ?? "",
    gender: voice.gender ?? "",
    age: voice.age ?? "",
  };
}

export function generationElapsedSeconds(startMs) {
  if (!startMs) return undefined;
  return Math.max(0, Math.round((Date.now() - startMs) / 1000));
}

export const NOISE_REDUCTION_ANALYTICS_EVENT_ID = "0022";

/** 降噪失败归因（见 docs/lipsync-analytics.md §4.22） */
export function resolveNoiseReductionFailReason(
  error,
  noiseReductionStage = false,
  uploadDetail = {},
) {
  if (!noiseReductionStage) {
    return {
      fail_reason: uploadDetail.failReason || uploadDetail.uploadStage || "upload_failed",
      massage: error?.message ? String(error.message) : "upload failed before noise reduction",
    };
  }
  const code = Number(error?.code);
  if (code === 100097) {
    return {
      fail_reason: "free_quota_exhausted",
      massage: "noise reduction free quota exhausted",
    };
  }
  if (code === 100100) {
    return {
      fail_reason: "insufficient_credits",
      massage: "insufficient credits",
    };
  }
  const msg = String(error?.message || "");
  if (msg === "noise_reduction_create_failed") {
    return { fail_reason: "create_task_failed", massage: msg };
  }
  if (msg === "noise_reduction_empty_result") {
    return { fail_reason: "empty_result", massage: msg };
  }
  if (msg === "noise_reduction_timeout") {
    return { fail_reason: "timeout", massage: msg };
  }
  if (msg === "noise_reduction_missing_task_id") {
    return { fail_reason: "missing_task_id", massage: msg };
  }
  if (msg.includes("noise_reduction_task_failed")) {
    return { fail_reason: "task_failed", massage: msg };
  }
  if (msg.includes("noise_reduction_poll_failed")) {
    return { fail_reason: "poll_failed", massage: msg };
  }
  return { fail_reason: "unknown", massage: msg || "noise reduction failed" };
}

/** `220022` — AI 降噪（见 docs/lipsync-analytics.md §4.22） */
export function trackNoiseReduction(sendLog, payload = {}, ctx = {}) {
  trackLipSyncEvent(
    sendLog,
    NOISE_REDUCTION_ANALYTICS_EVENT_ID,
    {
      type: "noise_reduction",
      status: payload.status === "success" ? "success" : "filed",
      source: payload.source === "record" ? "record" : "upload",
      upload_id: payload.upload_id ? String(payload.upload_id) : "",
      duration: Number(payload.duration) || 0,
      file_url: payload.file_url ? String(payload.file_url) : "",
      estimated_credits: Number(payload.estimated_credits) || 0,
      ...(payload.input_file_url ? { input_file_url: String(payload.input_file_url) } : {}),
      ...(payload.task_id ? { task_id: String(payload.task_id) } : {}),
      ...(payload.fail_reason ? { fail_reason: String(payload.fail_reason) } : {}),
      ...(payload.massage ? { massage: String(payload.massage) } : {}),
    },
    ctx,
  );
}

/** 用户在裁剪/录音弹窗开启降噪时上报 `220022`。 */
export function reportNoiseReductionIfRequested(
  sendLog,
  ctx,
  {
    requested = false,
    status = "filed",
    source = "upload",
    uploadId = "",
    duration = 0,
    fileUrl = "",
    inputFileUrl = "",
    estimatedCredits = 0,
    taskId = "",
    noiseReductionStage = false,
    error = null,
    uploadStage = "",
    failReason = "",
  } = {},
) {
  if (!requested) return;
  const payload = {
    status,
    source,
    upload_id: uploadId,
    duration,
    file_url: fileUrl,
    estimated_credits: estimatedCredits,
    ...(inputFileUrl ? { input_file_url: inputFileUrl } : {}),
    ...(taskId ? { task_id: taskId } : {}),
  };
  if (status !== "success") {
    const resolved = resolveNoiseReductionFailReason(error, noiseReductionStage, {
      uploadStage,
      failReason,
    });
    payload.fail_reason = resolved.fail_reason;
    payload.massage = resolved.massage;
  }
  trackNoiseReduction(sendLog, payload, ctx);
}

/** 在组件内创建 Lip Sync 埋点方法（见 docs/lipsync-analytics.md） */
export function createLipSyncTracker(nuxtApp, userStore, route) {
  const sendLog = nuxtApp?.$sendLog || (() => {});
  const ctx = () => ({ userStore, route });

  return {
    track(eventId, payload = {}) {
      trackLipSyncEvent(sendLog, eventId, payload, ctx());
    },
    loginDialogOpen(triggerType, from = "") {
      trackLipSyncEvent(
        sendLog,
        "0011",
        {
          type: "login_dialog_open",
          trigger_type: triggerType,
          ...(from ? { from } : {}),
        },
        ctx(),
      );
    },
    loginStatus(success, massage = "") {
      trackLipSyncEvent(
        sendLog,
        "0012",
        {
          type: "login_status",
          status: success ? "success" : "filed",
          ...(massage ? { massage: String(massage) } : {}),
        },
        ctx(),
      );
    },
    payDialogOpen(triggerType, from = "") {
      this.payDialogSession("enter", { trigger_type: triggerType, from });
    },
    payDialogSession(action, context = {}, durationSec = 0) {
      const triggerType = context.trigger_type ?? "click";
      const from = context.from ?? "";
      trackLipSyncEvent(
        sendLog,
        "0013",
        {
          type: "pay_dialog_open",
          action,
          trigger_type: triggerType,
          ...(from ? { from } : {}),
          ...(action === "leave" ? { duration: durationSec } : {}),
          ...(context.trigger_url ? { trigger_url: context.trigger_url } : {}),
          ...(context.trigger_route ? { trigger_route: context.trigger_route } : {}),
        },
        ctx(),
      );
    },
    payPlanClick({ surface, parent_page, plan } = {}) {
      trackLipSyncEvent(
        sendLog,
        "0019",
        {
          type: "pay_plan_click",
          surface,
          ...(parent_page ? { parent_page } : {}),
          ...(plan ? { plan } : {}),
        },
        ctx(),
      );
    },
    payStatus(success, massage = "", extras = {}) {
      const stored = readPayPlanClickContext();
      const plan = extras.plan ?? stored?.plan;
      const surface = extras.surface ?? stored?.surface;
      trackLipSyncEvent(
        sendLog,
        "0014",
        {
          type: "pay_status",
          status: success ? "success" : "filed",
          ...(massage ? { massage: String(massage) } : {}),
          ...(surface ? { surface } : {}),
          ...(plan ? { plan } : {}),
        },
        ctx(),
      );
    },
    pageOpen(pageType = "") {
      trackLipSyncEvent(
        sendLog,
        "0001",
        {
          type: "page_open",
          action: "enter",
          duration: 0,
          ...(pageType ? { page_type: pageType } : {}),
          ...(pageType === "pricing" && import.meta.client
            ? { referrer_url: document.referrer || "" }
            : {}),
        },
        ctx(),
      );
    },
    pageLeave(durationSec, pageType = "") {
      trackLipSyncEvent(
        sendLog,
        "0001",
        {
          type: "page_open",
          action: "leave",
          duration: durationSec,
          ...(pageType ? { page_type: pageType } : {}),
          ...(pageType === "pricing" && import.meta.client
            ? { referrer_url: document.referrer || "" }
            : {}),
        },
        ctx(),
      );
    },
    pageOpenLeave(durationSec, pageType = "") {
      trackLipSyncEvent(
        sendLog,
        "0001",
        {
          type: "page_open",
          action: "leave",
          duration: durationSec,
          ...(pageType ? { page_type: pageType } : {}),
        },
        ctx(),
      );
    },
    download(url, fileName = "video.mp4", taskId = "") {
      return downloadLipSyncVideoWithAnalytics(sendLog, ctx(), url, fileName, taskId);
    },
  };
}
